/**
 * Register Component - Usa Firebase Authentication
 * Registro en 2 pasos: Datos personales → Datos de empresa
 */

import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Avatar,
  IconButton,
  CircularProgress,
  Alert,
  Stack,
  InputAdornment,
  Collapse,
  Fade,
  Stepper,
  Step,
  StepLabel,
  Divider,
  LinearProgress,
} from "@mui/material";
import {
  Google as GoogleIcon,
  Visibility,
  VisibilityOff,
  Person as PersonIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Badge as BadgeIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { auth } from "../firebase";
import {
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  updateProfile,
} from "firebase/auth";

const API_URL = "https://turismo.nara.chat/api";

// Países y ciudades
const countryOptions = [
  { code: "CO", name: "Colombia" },
  { code: "MX", name: "México" },
  { code: "PA", name: "Panamá" },
];

const citiesByCountry = {
  CO: ["Bogotá", "Medellín", "Cali", "Barranquilla", "Cartagena", "Bucaramanga", "Pereira", "Santa Marta", "Manizales", "Cúcuta"],
  MX: ["Ciudad de México", "Guadalajara", "Monterrey", "Puebla", "Tijuana", "León", "Querétaro", "Cancún", "Mérida", "Toluca"],
  PA: ["Ciudad de Panamá", "Colón", "David", "Santiago", "Chitré", "La Chorrera", "Penonomé", "Aguadulce"],
};

// Validaciones
const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const getPasswordStrength = (password) => {
  let strength = 0;
  if (password.length >= 8) strength += 25;
  if (/[a-z]/.test(password)) strength += 25;
  if (/[A-Z]/.test(password)) strength += 25;
  if (/\d/.test(password)) strength += 25;
  return strength;
};

const getPasswordStrengthColor = (strength) => {
  if (strength <= 25) return "#ef4444";
  if (strength <= 50) return "#f59e0b";
  if (strength <= 75) return "#3b82f6";
  return "#10b981";
};

const getPasswordStrengthLabel = (strength) => {
  if (strength <= 25) return "Débil";
  if (strength <= 50) return "Regular";
  if (strength <= 75) return "Buena";
  return "Fuerte";
};

export default function Register() {
  const navigate = useNavigate();

  // Estado del stepper
  const [activeStep, setActiveStep] = useState(0);
  const steps = ["Datos personales", "Datos de empresa"];

  // Estados del formulario
  const [form, setForm] = useState({
    name: "",
    lastname: "",
    email: "",
    password: "",
    confirmPassword: "",
    company: "",
    address: "",
    city: "",
    country: "",
    tax_id: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Estados de validación
  const [emailExists, setEmailExists] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const emailCheckTimeout = useRef(null);

  // Tipo de registro: "nueva" o "existente"
  const [tipoRegistro, setTipoRegistro] = useState(null);
  const [nitEmpresaNombre, setNitEmpresaNombre] = useState("");
  const [nitValidado, setNitValidado] = useState(false);
  const [nitError, setNitError] = useState("");

  // Estados de carga y errores
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [pendingApproval, setPendingApproval] = useState(false);
  const [pendingEmpresa, setPendingEmpresa] = useState("");

  // Limpiar errores
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Verificar email en tiempo real
  const checkEmailExists = async (email) => {
    setCheckingEmail(true);
    try {
      const res = await fetch(`${API_URL}/users/check_email?email=${encodeURIComponent(email)}`);
      const data = await res.json();
      setEmailExists(data.exists);
    } catch {
      setEmailExists(false);
    }
    setCheckingEmail(false);
  };

  // Verificar NIT de empresa existente
  const verificarNit = async () => {
    const nit = form.tax_id.trim();
    if (!nit) { setNitError("Ingresa el NIT"); return; }
    setNitError("");
    setLoading(true);
    try {
      const res = await fetch(API_URL + "/auth/check-nit?tax_id=" + encodeURIComponent(nit));
      const data = await res.json();
      if (data.exists) {
        setNitValidado(true);
        setNitEmpresaNombre(data.empresa_nombre || "");
      } else {
        setNitError("No se encontró una empresa con ese NIT. Verifica o selecciona 'Empresa nueva'.");
        setNitValidado(false);
      }
    } catch {
      setNitError("Error al verificar. Intenta de nuevo.");
    }
    setLoading(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    setError("");

    if (name === "email") {
      setEmailExists(false);
      if (emailCheckTimeout.current) clearTimeout(emailCheckTimeout.current);
      if (validateEmail(value)) {
        emailCheckTimeout.current = setTimeout(() => checkEmailExists(value), 500);
      }
    }

    if (name === "tax_id") {
      setNitValidado(false);
      setNitError("");
    }

    if (name === "country") {
      setForm((f) => ({ ...f, country: value, city: "" }));
    }
  };

  // Validar paso 1
  const validateStep1 = () => {
    if (!form.name.trim() || !form.lastname.trim()) {
      setError("Por favor ingresa tu nombre y apellido");
      return false;
    }
    if (!form.email.trim() || !validateEmail(form.email)) {
      setError("Por favor ingresa un correo electrónico válido");
      return false;
    }
    if (emailExists) {
      setError("Este correo ya está registrado");
      return false;
    }
    if (getPasswordStrength(form.password) < 100) {
      setError("La contraseña debe tener mínimo 8 caracteres, una mayúscula, una minúscula y un número");
      return false;
    }
    if (form.password !== form.confirmPassword) {
      setError("Las contraseñas no coinciden");
      return false;
    }
    return true;
  };

  // Validar paso 2
  const validateStep2 = () => {
    if (!tipoRegistro) {
      setError("Selecciona si es empresa nueva o existente");
      return false;
    }
    if (tipoRegistro === "existente") {
      if (!nitValidado) {
        setError("Verifica el NIT primero");
        return false;
      }
      return true;
    }
    // Empresa nueva
    if (!form.tax_id.trim()) {
      setError("Por favor ingresa el NIT/RFC/RUT de tu empresa");
      return false;
    }
    if (!form.company.trim()) {
      setError("Por favor ingresa el nombre de tu empresa");
      return false;
    }
    if (!form.country) {
      setError("Por favor selecciona un país");
      return false;
    }
    if (!form.city) {
      setError("Por favor selecciona una ciudad");
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (activeStep === 0 && validateStep1()) {
      setActiveStep(1);
    }
  };

  const handleBack = () => {
    setActiveStep(0);
    setError("");
  };

  /**
   * Registrar con Firebase y backend
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateStep2()) return;

    setLoading(true);
    setError("");

    try {
      let idToken;
      // Si ya hay usuario autenticado en Firebase (viene de Google), usar ese token
      const currentUser = auth.currentUser;
      if (currentUser) {
        idToken = await currentUser.getIdToken();
      } else {
        // Registro con email/password: crear usuario en Firebase
        const userCredential = await createUserWithEmailAndPassword(auth, form.email, form.password);
        await updateProfile(userCredential.user, {
          displayName: `${form.name} ${form.lastname}`,
        });
        idToken = await userCredential.user.getIdToken();
      }

      const payload = { token: idToken };
      if (tipoRegistro === "nueva") {
        payload.company = {
          company: form.company,
          address: form.address,
          city: form.city,
          country: form.country,
          tax_id: form.tax_id,
        };
      } else {
        // Empresa existente: solo enviar tax_id para unirse
        payload.company = { tax_id: form.tax_id, company: "", address: "", city: "", country: "" };
      }

      const response = await fetch(API_URL + "/auth/firebase", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok) {
        if (data.status === "pending_approval") {
          // Usuario pendiente de aprobación
          setPendingApproval(true);
          setPendingEmpresa(data.empresa_nombre || "la empresa");
          setTimeout(() => navigate("/pending-approval", {
            state: { status: "pending_approval", empresa_nombre: data.empresa_nombre, name: form.name }
          }), 2500);
        } else {
          setSuccess(true);
          localStorage.setItem("user_id", data.user_id);
          setTimeout(() => navigate("/booking"), 2000);
        }
      } else {
        throw new Error(data.detail || "Error al registrar");
      }
    } catch (err) {
      console.error("Registration error:", err);

      if (err.code === "auth/email-already-in-use") {
        setError("Este correo ya está registrado. Intenta iniciar sesión.");
      } else if (err.code === "auth/weak-password") {
        setError("La contraseña es muy débil");
      } else {
        setError(err.message || "Error al registrar. Intenta de nuevo.");
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Registrar con Google
   */
  const handleGoogleRegister = async () => {
    setLoading(true);
    setError("");

    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: "select_account" });

      const result = await signInWithPopup(auth, provider);
      const idToken = await result.user.getIdToken();

      // Intentar autenticar - si es nuevo pedirá datos de empresa
      const response = await fetch(`${API_URL}/auth/firebase`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: idToken }),
      });

      const data = await response.json();

      if (response.ok) {
        if (data.status === "pending_approval") {
          navigate("/pending-approval", {
            state: { status: "pending_approval", empresa_nombre: data.empresa_nombre, name: data.name }
          });
          return;
        }
        if (data.status === "rejected") {
          navigate("/pending-approval", { state: { status: "rejected", name: data.name } });
          return;
        }
        localStorage.setItem("user_id", data.user_id);
        navigate("/booking");
      } else if (response.status === 422) {
        // Usuario nuevo con Google - necesita datos de empresa
        // Pre-llenar nombre del formulario
        const displayName = result.user.displayName || "";
        const nameParts = displayName.split(" ");

        setForm({
          ...form,
          name: nameParts[0] || "",
          lastname: nameParts.slice(1).join(" ") || "",
          email: result.user.email || "",
          password: "GoogleAuth123!", // Placeholder - no se usa
          confirmPassword: "GoogleAuth123!",
        });

        setActiveStep(1); // Ir directo al paso 2
      } else {
        throw new Error(data.detail || "Error al registrar");
      }
    } catch (err) {
      console.error("Google register error:", err);
      if (err.code !== "auth/popup-closed-by-user") {
        setError(err.message || "Error al registrar con Google");
      }
    } finally {
      setLoading(false);
    }
  };

  const selectedCountryCode = countryOptions.find((c) => c.name === form.country)?.code;
  const passwordStrength = getPasswordStrength(form.password);

  // Pantalla de éxito
  if (success) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          background: "linear-gradient(135deg, #164e63 0%, #0c4a6e 50%, #155e75 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
        }}
      >
        <Fade in timeout={500}>
          <Paper
            elevation={24}
            sx={{
              p: 5,
              borderRadius: 4,
              textAlign: "center",
              maxWidth: 400,
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 80, color: "#10b981", mb: 2 }} />
            <Typography variant="h5" fontWeight={700} color="#155e75" gutterBottom>
              ¡Registro exitoso!
            </Typography>
            <Typography color="#64748b" sx={{ mb: 3 }}>
              Tu cuenta ha sido creada. Serás redirigido al dashboard...
            </Typography>
            <CircularProgress size={24} sx={{ color: "#6366f1" }} />
          </Paper>
        </Fade>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", width: "100%", display: "flex", overflow: "hidden" }}>
      {process.env.REACT_APP_ENV === 'test' && (
        <Box sx={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
          bgcolor: '#f59e0b', color: '#fff', textAlign: 'center',
          py: 0.5, fontSize: 12, fontWeight: 700, letterSpacing: 1,
        }}>
          ENTORNO DE PRUEBAS
        </Box>
      )}

      {/* COLUMNA IZQUIERDA — Branding (solo desktop) */}
      <Box sx={{
        display: { xs: "none", md: "flex" },
        width: "40%", minHeight: "100vh", flexDirection: "column",
        justifyContent: "center", alignItems: "center", position: "relative",
        background: "linear-gradient(135deg, #164e63 0%, #0c4a6e 50%, #0891b2 100%)",
        p: 5,
      }}>
        <Box sx={{ position: "relative", zIndex: 1, textAlign: "center", maxWidth: 340 }}>
          <Box sx={{ width: 56, height: 56, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.1)", mx: "auto", mb: 3, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <img src="/images/logo-nara-png.png" alt="Nara" style={{ width: 48, height: 48, objectFit: "contain" }} />
          </Box>
          <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 800, fontSize: 28, color: "#fff", lineHeight: 1.2, mb: 1 }}>
            NARA Tourism
          </Typography>
          <Typography sx={{ fontSize: 15, color: "rgba(255,255,255,0.7)", mb: 3, lineHeight: 1.5 }}>
            Crea tu cuenta y comienza a vender pasadías con inteligencia artificial
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, textAlign: "left" }}>
            {[
              { icon: "✅", text: "Configura en minutos" },
              { icon: "🌴", text: "Tu chatbot vendiendo 24/7" },
              { icon: "📋", text: "Reservaciones automáticas" },
            ].map((item, i) => (
              <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Typography sx={{ fontSize: 18 }}>{item.icon}</Typography>
                <Typography sx={{ color: "rgba(255,255,255,0.8)", fontSize: 13 }}>{item.text}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
        <Box sx={{ position: "absolute", bottom: 30, opacity: 0.4 }}>
          <Typography sx={{ color: "#fff", fontSize: 11 }}>
            ¿Ya tienes cuenta?{" "}
            <Typography component="span" sx={{ fontWeight: 700, cursor: "pointer", textDecoration: "underline" }}
              onClick={() => navigate("/login")}>
              Inicia sesión
            </Typography>
          </Typography>
        </Box>
      </Box>

      {/* COLUMNA DERECHA — Formulario */}
      <Box sx={{
        flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
        bgcolor: "#f0fdfa", p: { xs: 2, sm: 3 }, overflowY: "auto",
      }}>
        <Box sx={{ width: "100%", maxWidth: 480 }}>
          {/* Logo mobile only */}
          <Box sx={{ display: { xs: "flex", md: "none" }, justifyContent: "center", mb: 2 }}>
            <Box sx={{ width: 48, height: 48, borderRadius: "50%", bgcolor: "#164e63", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <img src="/images/logo-nara-png.png" alt="Nara" style={{ width: 40, height: 40, objectFit: "contain" }} />
            </Box>
          </Box>

          <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: 22, color: "#134e4a", mb: 0.5, textAlign: { xs: "center", md: "left" } }}>
            Crear cuenta
          </Typography>
          <Typography sx={{ fontSize: 13, color: "#64748b", mb: 2, textAlign: { xs: "center", md: "left" } }}>
            Comienza a usar NARA Tourism gratis
          </Typography>

          {/* Stepper */}
          <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Mensajes de error */}
          <Collapse in={!!error}>
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError("")}>
              {error}
            </Alert>
          </Collapse>

          {/* Paso 1: Datos personales */}
          <Collapse in={activeStep === 0}>
            <form onSubmit={(e) => { e.preventDefault(); handleNext(); }}>
              <Stack spacing={2}>
                {/* Nombre y Apellido */}
                <Box sx={{ display: "flex", gap: 2 }}>
                  <TextField
                    label="Nombre"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    fullWidth
                    size="small"
                    disabled={loading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon sx={{ color: "#94a3b8", fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        background: "#f8fafc",
                      },
                    }}
                  />
                  <TextField
                    label="Apellido"
                    name="lastname"
                    value={form.lastname}
                    onChange={handleChange}
                    fullWidth
                    size="small"
                    disabled={loading}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        background: "#f8fafc",
                      },
                    }}
                  />
                </Box>

                {/* Email */}
                <TextField
                  label="Correo electrónico"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  disabled={loading}
                  error={emailExists}
                  helperText={
                    checkingEmail
                      ? "Verificando..."
                      : emailExists
                      ? "Este correo ya está registrado"
                      : ""
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon sx={{ color: "#94a3b8", fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      background: "#f8fafc",
                    },
                  }}
                />

                {/* Contraseña */}
                <Box>
                  <TextField
                    label="Contraseña"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    value={form.password}
                    onChange={handleChange}
                    fullWidth
                    size="small"
                    disabled={loading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon sx={{ color: "#94a3b8", fontSize: 20 }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowPassword(!showPassword)} size="small">
                            {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        background: "#f8fafc",
                      },
                    }}
                  />
                  {/* Indicador de fortaleza */}
                  {form.password && (
                    <Box sx={{ mt: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={passwordStrength}
                        sx={{
                          height: 4,
                          borderRadius: 2,
                          bgcolor: "#e2e8f0",
                          "& .MuiLinearProgress-bar": {
                            bgcolor: getPasswordStrengthColor(passwordStrength),
                          },
                        }}
                      />
                      <Typography variant="caption" sx={{ color: getPasswordStrengthColor(passwordStrength) }}>
                        {getPasswordStrengthLabel(passwordStrength)}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Confirmar contraseña */}
                <TextField
                  label="Confirmar contraseña"
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={form.confirmPassword}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  disabled={loading}
                  error={form.confirmPassword && form.password !== form.confirmPassword}
                  helperText={
                    form.confirmPassword && form.password !== form.confirmPassword
                      ? "Las contraseñas no coinciden"
                      : ""
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon sx={{ color: "#94a3b8", fontSize: 20 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} size="small">
                          {showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      background: "#f8fafc",
                    },
                  }}
                />

                {/* Botón siguiente */}
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  size="large"
                  disabled={loading || checkingEmail || emailExists}
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    py: 1.3,
                    borderRadius: 2,
                    fontWeight: 700,
                    textTransform: "none",
                    background: "linear-gradient(135deg, #164e63 0%, #0891b2 100%)",
                  }}
                >
                  Siguiente
                </Button>
              </Stack>
            </form>

            {/* Divider y Google */}
            <Divider sx={{ my: 2.5 }}>
              <Typography variant="caption" color="#94a3b8">o regístrate con</Typography>
            </Divider>

            <Button
              variant="outlined"
              fullWidth
              startIcon={<GoogleIcon />}
              onClick={handleGoogleRegister}
              disabled={loading}
              sx={{
                py: 1.2,
                borderRadius: 2,
                fontWeight: 600,
                textTransform: "none",
                borderColor: "#e2e8f0",
                color: "#334155",
              }}
            >
              Google
            </Button>
          </Collapse>

          {/* Paso 2: Datos de empresa */}
          <Collapse in={activeStep === 1}>
            <form onSubmit={handleSubmit}>
              <Stack spacing={2}>

                {/* Pregunta: empresa nueva o existente */}
                {!tipoRegistro && (
                  <>
                    <Typography sx={{ color: "#334155", fontSize: 14, fontWeight: 600, textAlign: "center" }}>
                      ¿Tu empresa ya está registrada en NARA?
                    </Typography>
                    <Box sx={{ display: "flex", gap: 2 }}>
                      <Button fullWidth variant="outlined" onClick={() => setTipoRegistro("nueva")}
                        sx={{ py: 2, borderRadius: 2, fontWeight: 600, textTransform: "none", borderColor: "#e2e8f0", color: "#334155" }}>
                        <Box sx={{ textAlign: "center" }}>
                          <BusinessIcon sx={{ fontSize: 28, color: "#10b981", mb: 0.5 }} /><br />
                          Empresa nueva
                          <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.5 }}>Registrar una empresa por primera vez</Typography>
                        </Box>
                      </Button>
                      <Button fullWidth variant="outlined" onClick={() => setTipoRegistro("existente")}
                        sx={{ py: 2, borderRadius: 2, fontWeight: 600, textTransform: "none", borderColor: "#e2e8f0", color: "#334155" }}>
                        <Box sx={{ textAlign: "center" }}>
                          <PersonIcon sx={{ fontSize: 28, color: "#3b82f6", mb: 0.5 }} /><br />
                          Empresa existente
                          <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.5 }}>Unirme a una empresa ya registrada</Typography>
                        </Box>
                      </Button>
                    </Box>
                  </>
                )}

                {/* FLUJO: Empresa existente — solo pedir NIT */}
                {tipoRegistro === "existente" && (
                  <>
                    <Typography sx={{ color: "#334155", fontSize: 13, fontWeight: 600 }}>
                      Ingresa el NIT de la empresa a la que deseas unirte
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <TextField
                        label="NIT / RFC / RUT"
                        name="tax_id"
                        value={form.tax_id}
                        onChange={handleChange}
                        fullWidth
                        size="small"
                        disabled={loading || nitValidado}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <BadgeIcon sx={{ color: "#94a3b8", fontSize: 20 }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, background: "#f8fafc" } }}
                      />
                      {!nitValidado && (
                        <Button variant="contained" onClick={verificarNit} disabled={loading || !form.tax_id.trim()}
                          sx={{ minWidth: 100, borderRadius: 2, textTransform: "none", fontWeight: 600, background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)" }}>
                          {loading ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Verificar"}
                        </Button>
                      )}
                    </Box>
                    {nitError && (
                      <Alert severity="error" sx={{ borderRadius: 2, fontSize: 13 }}>{nitError}</Alert>
                    )}
                    {nitValidado && (
                      <Alert severity="success" sx={{ borderRadius: 2, fontSize: 13 }}>
                        Empresa encontrada: <strong>{nitEmpresaNombre}</strong>.
                        Al crear tu cuenta, se enviará una solicitud de acceso al administrador de la empresa.
                      </Alert>
                    )}
                  </>
                )}

                {/* FLUJO: Empresa nueva — todos los datos */}
                {tipoRegistro === "nueva" && (
                  <>
                    <TextField label="NIT / RFC / RUT" name="tax_id" value={form.tax_id} onChange={handleChange}
                      fullWidth size="small" disabled={loading}
                      InputProps={{ startAdornment: (<InputAdornment position="start"><BadgeIcon sx={{ color: "#94a3b8", fontSize: 20 }} /></InputAdornment>) }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, background: "#f8fafc" } }} />
                    <TextField label="Nombre de la empresa" name="company" value={form.company} onChange={handleChange}
                      fullWidth size="small" disabled={loading}
                      InputProps={{ startAdornment: (<InputAdornment position="start"><BusinessIcon sx={{ color: "#94a3b8", fontSize: 20 }} /></InputAdornment>) }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, background: "#f8fafc" } }} />
                    <TextField label="Dirección (opcional)" name="address" value={form.address} onChange={handleChange}
                      fullWidth size="small" disabled={loading}
                      InputProps={{ startAdornment: (<InputAdornment position="start"><LocationIcon sx={{ color: "#94a3b8", fontSize: 20 }} /></InputAdornment>) }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, background: "#f8fafc" } }} />
                    <Box sx={{ display: "flex", gap: 2 }}>
                      <TextField select label="País" name="country" value={form.country} onChange={handleChange}
                        fullWidth size="small" disabled={loading} SelectProps={{ native: true }}
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, background: "#f8fafc" } }}>
                        <option value="">Seleccionar</option>
                        {countryOptions.map((c) => (<option key={c.code} value={c.name}>{c.name}</option>))}
                      </TextField>
                      <TextField select label="Ciudad" name="city" value={form.city} onChange={handleChange}
                        fullWidth size="small" disabled={loading || !form.country} SelectProps={{ native: true }}
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, background: "#f8fafc" } }}>
                        <option value="">Seleccionar</option>
                        {(citiesByCountry[selectedCountryCode] || []).map((city) => (<option key={city} value={city}>{city}</option>))}
                      </TextField>
                    </Box>
                  </>
                )}

                {/* Botones — solo si ya eligió tipo */}
                {tipoRegistro && (
                  <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
                    <Button variant="outlined" fullWidth
                      onClick={() => { setTipoRegistro(null); setNitValidado(false); setNitError(""); setError(""); }}
                      disabled={loading} startIcon={<ArrowBackIcon />}
                      sx={{ py: 1.2, borderRadius: 2, fontWeight: 600, textTransform: "none", borderColor: "#e2e8f0", color: "#64748b" }}>
                      Atrás
                    </Button>
                    <Button type="submit" variant="contained" fullWidth
                      disabled={loading || (tipoRegistro === "existente" && !nitValidado)}
                      sx={{ py: 1.2, borderRadius: 2, fontWeight: 700, textTransform: "none",
                        background: tipoRegistro === "existente"
                          ? "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)"
                          : "linear-gradient(135deg, #10b981 0%, #059669 100%)" }}>
                      {loading ? <CircularProgress size={24} sx={{ color: "#fff" }} />
                        : tipoRegistro === "existente" ? "Solicitar acceso" : "Crear cuenta"}
                    </Button>
                  </Box>
                )}

                {/* Mensaje de solicitud pendiente */}
                {pendingApproval && (
                  <Alert severity="success" sx={{ borderRadius: 2 }}>
                    Tu solicitud de acceso a <strong>{pendingEmpresa}</strong> fue enviada.
                    El administrador la revisará pronto.
                  </Alert>
                )}
              </Stack>
            </form>
          </Collapse>

          {/* Link a login */}
          <Box sx={{ textAlign: "center", mt: 3 }}>
            <Typography variant="body2" color="#64748b">
              ¿Ya tienes cuenta?{" "}
              <Typography
                component="span"
                sx={{
                  color: "#6366f1",
                  fontWeight: 600,
                  cursor: "pointer",
                  "&:hover": { textDecoration: "underline" },
                }}
                onClick={() => navigate("/login")}
              >
                Inicia sesión
              </Typography>
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
